import parse, { domToReact, Element, type DOMNode } from "html-react-parser";
import Showdown from "showdown";

// Helper to convert text to a stable kebab-case slug for test ids
function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "") // Remove special characters except hyphens
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Collapse multiple hyphens
    .slice(0, 50); // Limit length for readability
}

// Extract text content from DOM children
function extractTextFromChildren(children: DOMNode[]): string {
  return children
    .map(child => {
      if (child.type === "text" && "data" in child) {
        return child.data;
      }
      if (child instanceof Element && child.children) {
        return extractTextFromChildren(child.children as DOMNode[]);
      }
      return "";
    })
    .join("");
}

// Showdown converter instance for markdown to HTML conversion
const markdownConverter = new Showdown.Converter({
  simplifiedAutoLink: true,
  excludeTrailingPunctuationFromURLs: true,
  strikethrough: true,
  tables: true,
  tasklists: true,
  openLinksInNewWindow: true
});

// Helper to render hex color with preview swatch
function HexColorPreview({ hex }: { hex: string }) {
  return (
    <span className="inline-flex items-baseline gap-[1px]">
      <span
        className="inline-block h-3 w-3 rounded border border-gray-300"
        style={{ backgroundColor: hex }}
        title={hex}
      />
      <span>{hex}</span>
    </span>
  );
}

// Process text nodes to find and render hex color codes
export function processTextWithHexCodes(text: string): React.ReactNode {
  const hexPattern = /#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{4}|[0-9A-Fa-f]{8})\b/g;
  const parts: Array<{ type: "text" | "hex"; content: string }> = [];
  let lastIndex = 0;
  let match = hexPattern.exec(text);

  while (match !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", content: text.slice(lastIndex, match.index) });
    }
    parts.push({ type: "hex", content: match[0] });
    lastIndex = match.index + match[0].length;
    match = hexPattern.exec(text);
  }

  if (lastIndex < text.length) {
    parts.push({ type: "text", content: text.slice(lastIndex) });
  }

  if (parts.length === 0) {
    return text;
  }

  return parts.map((part, idx) =>
    part.type === "hex" ? (
      <HexColorPreview key={idx} hex={part.content} />
    ) : (
      <span key={idx}>{part.content}</span>
    )
  );
}

// Component to render markdown content with hex color previews
export function MarkdownContent({ content }: { content: string }) {
  const html = markdownConverter.makeHtml(content);

  // Custom replace function to process text nodes for hex colors
  const options = {
    replace: (domNode: DOMNode) => {
      // Process text nodes for hex color codes
      if (domNode.type === "text" && "data" in domNode) {
        const processed = processTextWithHexCodes(domNode.data);
        if (processed !== domNode.data) {
          return <>{processed}</>;
        }
      }

      // Add styling to specific elements
      if (domNode instanceof Element) {
        const { name, children, attribs } = domNode;

        // Style code blocks
        if (name === "code") {
          return (
            <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-sm">
              {domToReact(children as DOMNode[], options)}
            </code>
          );
        }

        // Style pre blocks
        if (name === "pre") {
          return (
            <pre className="overflow-x-auto rounded-md bg-gray-100 p-2 text-sm">
              {domToReact(children as DOMNode[], options)}
            </pre>
          );
        }

        // Style links
        if (name === "a") {
          // Derive stable slug from href or link text
          const href = attribs["href"] || "";
          const linkText = extractTextFromChildren(children as DOMNode[]);
          const slugSource = href || linkText;
          const slug = slugify(slugSource) || "link";
          return (
            <a
              href={attribs["href"]}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline hover:text-blue-800"
              data-testid={`markdown-link-${slug}`}
            >
              {domToReact(children as DOMNode[], options)}
            </a>
          );
        }

        // Style lists
        if (name === "ul") {
          return (
            <ul className="ml-4 list-disc space-y-1">
              {domToReact(children as DOMNode[], options)}
            </ul>
          );
        }

        if (name === "ol") {
          return (
            <ol className="ml-4 list-decimal space-y-1">
              {domToReact(children as DOMNode[], options)}
            </ol>
          );
        }

        // Style blockquotes
        if (name === "blockquote") {
          return (
            <blockquote className="border-l-4 border-gray-300 pl-3 italic text-gray-600">
              {domToReact(children as DOMNode[], options)}
            </blockquote>
          );
        }

        // Style strong/bold
        if (name === "strong" || name === "b") {
          return (
            <strong className="font-semibold">{domToReact(children as DOMNode[], options)}</strong>
          );
        }

        // Style paragraphs with proper spacing
        if (name === "p") {
          return <p className="mb-2 last:mb-0">{domToReact(children as DOMNode[], options)}</p>;
        }
      }

      return undefined;
    }
  };

  return (
    <div className="markdown-content text-inherit" data-testid="markdown-content">
      {parse(html, options)}
    </div>
  );
}
